/**
 * @fileoverview Redux Toolkit Query API for aVenture API
 * @active CONFIRMED - This API is actively used in the application
 * @preferred PREFERRED - This is the recommended API service for new data fetching
 * 
 * This module configures the Redux Toolkit Query API for the aVenture API.
 * It provides a centralized way to make API requests and manage the response data
 * with automatic caching, loading states, and cache invalidation.
 * 
 * WHEN TO USE:
 * - For new data fetching requirements
 * - When you need automatic caching and cache invalidation
 * - For components that need to track loading/error states
 * - When working with React components that need to react to data changes
 * 
 * This is the foundation for all RTK Query endpoints in the application.
 * Other service files extend this base API using .injectEndpoints().
 * 
 * Benefits over direct axios usage:
 * - Automatic caching and cache invalidation
 * - Automatic loading and error states
 * - Automatic re-fetching
 * - TypeScript integration
 * - Centralized state management
 * 
 * @see {@link src/utils/urlUtils.ts} - URL formatting utilities used for consistent URL handling
 * @see {@link src/services/apiService/axiosService.ts} - For direct axios requests
 * @see {@link src/utils/functionForBack/getDataByAxios.ts} - For simplified axios requests
 * @see {@link src/services/companies.service.ts} - Example of extending this API
 * @see {@link src/services/funds.service.ts} - Example of extending this API
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getApiBaseUrl } from "@/utils/urlUtils";

export const aventureApi = createApi({
  reducerPath: "aventureApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(), // Use the utility function to ensure consistent URL formatting
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      
      // Add Origin header to prevent CORS issues in browser environment
      if (typeof window !== 'undefined') {
        headers.set("Origin", window.location.origin);
      }
      
      // Add any authorization headers if needed
      return headers;
    },
    credentials: 'include', // Send cookies with cross-origin requests
  }),
  tagTypes: [
    "search",
    "companies",
    "funds",
    "fundraising",
    "people",
    "investors",
    "fundPersons",
    "fundInvestment",
    "tags",
    "profile",
    "filters",
    "notifications-settings",
    "notifications",
    "admin",
    "views",
    "listByView",
    "lists",
    "newViews",
    "companiesList",
  ],
  endpoints: () => ({}),
});
